/**
 * Centralized unDraw illustration registry for WebFoxx CUET UG & PG.
 * Single source of truth for visual asset management.
 */

export type IllustrationEntry = {
  file: string;
  alt: string;
};

export const ILLUSTRATION_NAMESPACES = [
  'page', 'hero', 'feature', 'card', 'empty', 'error', 'dashboard', 'admin',
  'results', 'jobs', 'college', 'academy', 'vault', 'support',
  'profile', 'settings', 'notification', 'onboarding', 'pricing', 'faq',
] as const;

export const ILLUSTRATION_REGISTRY: Record<string, IllustrationEntry> = {
  // —— page.* ——
  'page.home': { file: 'student-learning.svg', alt: 'Your CUET UG & PG preparation journey' },
  'page.results': { file: 'exam-results.svg', alt: 'CUET Mock Test Results' },
  'page.vault': { file: 'document-vault.svg', alt: 'Student document vault' },
  'page.admit-cards': { file: 'online-exam.svg', alt: 'Admit cards and hall tickets' },
  'page.auth': { file: 'sign-in.svg', alt: 'Sign in to your account' },
  'page.dashboard': { file: 'dashboard-overview.svg', alt: 'Your CUET exam command center' },
  'page.notifications': { file: 'notifications-updates.svg', alt: 'Exam notifications' },
  'page.settings': { file: 'organized-content.svg', alt: 'Settings and preferences' },
  'page.jobs': { file: 'student-learning.svg', alt: 'University admissions and opportunities' },
  'page.college': { file: 'student-learning.svg', alt: 'College selection and cutoff analysis' },

  // —— hero.* (eager-loaded) ——
  'hero.home': { file: 'student-learning.svg', alt: 'WebFoxx CUET UG & PG Preparation' },
  'hero.results': { file: 'exam-results.svg', alt: 'Mock test & performance analytics' },
  'hero.vault': { file: 'document-vault.svg', alt: 'Student document vault' },
  'hero.admit-cards': { file: 'online-exam.svg', alt: 'Admit cards and examination tickets' },
  'hero.dashboard': { file: 'dashboard-overview.svg', alt: 'CUET preparation command center' },
  'hero.notifications': { file: 'notifications-updates.svg', alt: 'Live exam updates & announcements' },
  'hero.timeline': { file: 'exam-timeline.svg', alt: 'CUET UG & PG examination timeline' },

  // —— card.* ——
  'card.result': { file: 'exam-results.svg', alt: 'Test result summary' },
  'card.results': { file: 'exam-results.svg', alt: 'Test results' },
  'card.admit-cards': { file: 'online-exam.svg', alt: 'Hall tickets' },
  'card.vault': { file: 'document-vault.svg', alt: 'Document storage' },
  'card.timeline': { file: 'exam-timeline.svg', alt: 'Exam timeline' },
  'card.categories': { file: 'student-learning.svg', alt: 'Subject categories' },
  'card.mock-test': { file: 'mock-test.svg', alt: 'CUET mock test series' },
  'card.document': { file: 'document-vault.svg', alt: 'Certificates and study notes' },
  'card.course': { file: 'student-learning.svg', alt: 'Subject course module' },

  // —— feature.* ——
  'feature.results': { file: 'exam-results.svg', alt: 'Detailed scorecards and merit list' },
  'feature.admit-cards': { file: 'online-exam.svg', alt: 'Admit cards' },
  'feature.vault': { file: 'secure-files.svg', alt: 'Encrypted document vault' },
  'feature.timeline': { file: 'progress-tracking.svg', alt: 'Track study progress' },
  'feature.mock-test': { file: 'mock-test.svg', alt: 'Paper I & Paper II practice' },
  'feature.certificate': { file: 'certificate.svg', alt: 'Certificates and badges' },
  'feature.search': { file: 'file-search.svg', alt: 'Question bank search' },

  // —— empty.* ——
  'empty.default': { file: 'no-results.svg', alt: 'Nothing here yet' },
  'empty.search': { file: 'empty-search.svg', alt: 'No questions or subjects found' },
  'empty.results': { file: 'exam-results.svg', alt: 'No test attempts yet' },
  'empty.notifications': { file: 'no-notification.svg', alt: 'No notifications' },
  'empty.vault': { file: 'document-vault.svg', alt: 'No documents uploaded yet' },
  'empty.admit-cards': { file: 'online-exam.svg', alt: 'No admit cards available' },
  'empty.saved-exams': { file: 'student-learning.svg', alt: 'No bookmarked subjects' },
  'empty.timeline': { file: 'exam-timeline.svg', alt: 'No upcoming exam events' },

  // —— error.* ——
  'error.404': { file: '404-not-found.svg', alt: 'Page not found' },
  'error.500': { file: 'maintenance.svg', alt: 'System maintenance' },
  'error.network': { file: 'maintenance.svg', alt: 'Connection error' },

  // —— dashboard.* ——
  'dashboard.overview': { file: 'dashboard-overview.svg', alt: 'CUET performance overview' },

  // —— admin.* ——
  'admin.hero': { file: 'dashboard-overview.svg', alt: 'CMS Admin Center' },
  'admin.kpi.default': { file: 'dashboard-overview.svg', alt: 'Platform metrics' },
  'admin.kpi.users': { file: 'authentication.svg', alt: 'Registered candidates' },
  'admin.kpi.results': { file: 'exam-results.svg', alt: 'Published test results' },

  // —— vault.* ——
  'vault.default': { file: 'document-vault.svg', alt: 'Vault document' },
  'vault.identity': { file: 'secure-files.svg', alt: 'Identity document' },
  'vault.education': { file: 'student-learning.svg', alt: 'Educational certificate' },
  'vault.certificate': { file: 'certificate.svg', alt: 'CUET qualification certificate' },
  'vault.photo': { file: 'authentication.svg', alt: 'Candidate photograph' },

  // —— support.* ——
  'support.help': { file: 'help-support.svg', alt: 'Faculty help and support' },

  // —— onboarding.* ——
  'onboarding.auth': { file: 'sign-in.svg', alt: 'Welcome to WebFoxx Academy' },

  // —— notification.* ——
  'notification.updates': { file: 'notifications-updates.svg', alt: 'CUET updates and news' },

  // —— faq.* ——
  'faq.general': { file: 'faq.svg', alt: 'Frequently asked questions' },
};

const ASSETS_BASE = '/assets/illustrations';

export function isEagerIllustrationContext(context: string): boolean {
  return context.startsWith('hero.');
}

export function resolveIllustrationContext(context: string): IllustrationEntry | null {
  const entry = ILLUSTRATION_REGISTRY[context];
  if (!entry && process.env.NODE_ENV === 'development') {
    console.warn(`[Illustration] Context "${context}" not found in registry.`);
  }
  return entry ?? null;
}

export function getIllustrationAssetUrl(file: string): string {
  return `${ASSETS_BASE}/${file}`;
}
